import { FC, Suspense } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { DashboardLayout } from '../modules/dashboard/components/DashboardLayout'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { WithChildren } from '../../_metronic/helpers'
import { ReportLayout } from '../modules/report/ReportLayout'
import { ClientLayout } from '../modules/client/ClientLayout'
import { AssetLayout } from '../modules/asset/AssetLayout'
import { ClientDetailsPage } from '../modules/client/pages/ClientDetailsPage'
import { ClientEditPage } from '../modules/client/pages/ClientEditPage'
import { ClientNewPage } from '../modules/client/pages/ClientNewPage'
import { AssetDetailsPage } from '../modules/asset/pages/AssetDetailsPage'
import { AssetEditPage } from '../modules/asset/pages/AssetEditPage'
import { AssetNewPage } from '../modules/asset/pages/AssetNewPage'
import { ReportNewPage } from '../modules/report/page/ReportNewPage'
import { ReportDetailsPage } from '../modules/report/page/ReportDetailsPage'
import { ReportEditPage } from '../modules/report/page/ReportEditPage'
import { ContractLayout } from '../modules/contract/ContractLayout'
import { ContractEditPage } from '../modules/contract/pages/ContractEditPage'
import { ContractDetailsPage } from '../modules/contract/pages/ContractDetailsPage'
import { ContractNewPage } from '../modules/contract/pages/ContractNewPage'
import { ContactLayout } from '../modules/contact/ContactLayout'
import { ContactDetailsPage } from '../modules/contact/pages/ContactDetailsPage'
import { ContactNewPage } from '../modules/contact/pages/ContactNewPage'
import { ContactEditPage } from '../modules/contact/pages/ContactEditPage'
import { TicketLayout } from '../modules/helpdesk/ticket/TicketLayout'
import { TicketEditPage } from '../modules/helpdesk/ticket/pages/TicketEditPage'
import { TicketNewPage } from '../modules/helpdesk/ticket/pages/TicketNewPage'
import { TicketDetailsPage } from '../modules/helpdesk/ticket/pages/TicketDetailsPage'
import { TaskNewPage } from '../modules/helpdesk/task/pages/TaskNewPage'
import { TaskDetailsPage } from '../modules/helpdesk/task/pages/TaskDetailsPage'
import { TaskEditPage } from '../modules/helpdesk/task/pages/TaskEditPage'
import { useAppSelector } from '../modules/store/Store'
import { selectAuth } from '../modules/auth/Auth.slice'
import { menuVisibilityService } from '../service/MenuVisibility.service'

const PrivateRoutes = () => {
  // const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const auth = useAppSelector(selectAuth)
  const canAccessDirectoryRoutes = menuVisibilityService.canAccessDirectoryRoutes(auth?.tokens?.idToken)

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        {/* <Route path='auth/*' element={<Navigate to='/dashboard' />} /> */}
        <Route path='auth/*' element={<Navigate to='/reports' />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardLayout />} />
        <Route
          path='/contacts/new'
          element={canAccessDirectoryRoutes ? <ContactNewPage /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='/contacts/:contactId'
          element={canAccessDirectoryRoutes ? <ContactDetailsPage /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='/contacts/:contactId/edit'
          element={canAccessDirectoryRoutes ? <ContactEditPage /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='contacts'
          element={canAccessDirectoryRoutes ? <ContactLayout /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='/clients/new'
          element={canAccessDirectoryRoutes ? <ClientNewPage /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='/clients/:clientId'
          element={canAccessDirectoryRoutes ? <ClientDetailsPage /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='/clients/:clientId/edit'
          element={canAccessDirectoryRoutes ? <ClientEditPage /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='clients'
          element={canAccessDirectoryRoutes ? <ClientLayout /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='/contracts/new'
          element={canAccessDirectoryRoutes ? <ContractNewPage /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='/contracts/:contractId'
          element={canAccessDirectoryRoutes ? <ContractDetailsPage /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='/contracts/:contractId/edit'
          element={canAccessDirectoryRoutes ? <ContractEditPage /> : <Navigate to='/reports' replace />}
        />
        <Route
          path='contracts'
          element={canAccessDirectoryRoutes ? <ContractLayout /> : <Navigate to='/reports' replace />}
        />
        <Route path='/assets/new' element={<AssetNewPage />} />
        <Route path='/assets/:assetId' element={<AssetDetailsPage />} />
        <Route path='/assets/:assetId/edit' element={<AssetEditPage />} />
        <Route path='assets' element={<AssetLayout />} />
        <Route path='/reports/new' element={<ReportNewPage />} />
        <Route path='/reports/:reportId' element={<ReportDetailsPage />} />
        <Route path='/reports/:reportId/edit' element={<ReportEditPage />} />
        <Route path='reports' element={<ReportLayout />} />
        <Route path='/tickets/new' element={<TicketNewPage />} />
        <Route path='/tickets/:ticketId' element={<TicketDetailsPage />} />
        <Route path='/tickets/:ticketId/edit' element={<TicketEditPage />} />
        <Route path='/tickets' element={<TicketLayout />} />
        <Route path='/tickets/:ticketId/tasks/new' element={<TaskNewPage />} />
        <Route path='/tickets/:ticketId/tasks/:taskId' element={<TaskDetailsPage />} />
        <Route path='/tickets/:ticketId/tasks/:taskId/edit' element={<TaskEditPage />} />
        {/* Lazy Modules */}
        {/* <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
